// Scene queries + mutations for the Shot Builder.
// Replaces the mock `getScenesWithShots` / `mockDataStore` scene methods.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { logger } from "../../services/logger";
import { generateUUID } from "../../utils/uuid";
import { showErrorToast } from "@/utils/showErrorToast";
import {
  getScenes,
  createScene,
  updateScene,
  deleteScene,
  reorderScenes,
} from "@/services/api/sdk.gen";
import type { SceneWithShots } from "@/types/shots";
import { shotKeys } from "./shotKeys";
import { unwrapData } from "./unwrap";

/** Fetch the project's scenes, each with its nested shots. */
export function useScenes(projectId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: shotKeys.scenes(projectId ?? ""),
    queryFn: async () => {
      if (!projectId) throw new Error("Project ID is required");
      const correlationId = generateUUID();
      logger.info("Fetching scenes", { projectId, correlationId });
      const result = await getScenes({
        path: { projectId },
        throwOnError: true,
      });
      return unwrapData<SceneWithShots[]>(result, []);
    },
    enabled: !!user && !!projectId,
    staleTime: 30 * 1000,
  });
}

interface CreateSceneArgs {
  name: string;
  description?: string;
}

export function useCreateScene(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSceneArgs) => {
      const result = await createScene({
        path: { projectId },
        body: input,
        throwOnError: true,
      });
      return unwrapData<SceneWithShots | null>(result, null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shotKeys.scenes(projectId) });
    },
    onError: (error) => {
      showErrorToast({ message: "Failed to create scene", error, projectId });
    },
  });
}

interface UpdateSceneArgs {
  sceneId: string;
  name?: string;
  description?: string;
}

export function useUpdateScene(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sceneId, ...body }: UpdateSceneArgs) => {
      const result = await updateScene({
        path: { sceneId },
        body,
        throwOnError: true,
      });
      return unwrapData<SceneWithShots | null>(result, null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shotKeys.scenes(projectId) });
    },
    onError: (error) => {
      showErrorToast({ message: "Failed to update scene", error, projectId });
    },
  });
}

export function useDeleteScene(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sceneId: string) => {
      await deleteScene({ path: { sceneId }, throwOnError: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shotKeys.scenes(projectId) });
    },
    onError: (error) => {
      showErrorToast({ message: "Failed to delete scene", error, projectId });
    },
  });
}

export function useReorderScenes(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sceneIds: string[]) => {
      await reorderScenes({
        path: { projectId },
        body: { sceneIds },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shotKeys.scenes(projectId) });
    },
    onError: (error) => {
      showErrorToast({ message: "Failed to reorder scenes", error, projectId });
    },
  });
}
